import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import type { WhatsappGroup } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { WhatsappGroupResponseMode, WhatsappGroupStatus } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { Badge, Card, IconButton, Input, Switch, Toast, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { GroupActionError, useWhatsappGroupActions } from '../../settings/shared/settings-queries';
import { modeLabelKey, whatsappGroupBadge } from '../../settings/shared/whatsapp-group-status';
import { InviteLinkRows } from './InviteLinkRows';
import { useWhatsappGroupSubtitle } from './use-whatsapp-labels';
import { WHATSAPP_PAGE } from './constants';
import styles from './GroupSettingsPanel.module.css';

// The body of the "Group Settings" modal (Figma group-settings redesign): status badge + name (with
// the inline rename affordance) + subtitle, the response-mode switch and its caption, and the
// invite-link divider list. Close + Delete live in the modal footer (GroupSettingsModal), so the
// panel is content only.
export function GroupSettingsPanel({ group }: { group: WhatsappGroup }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const subtitle = useWhatsappGroupSubtitle();
  const { logPageClickBi } = useGenericBi();
  const { setResponseMode } = useWhatsappGroupActions();
  const [toast, setToast] = useState<string | null>(null);
  const badge = whatsappGroupBadge(group.status);
  const pending = group.status === WhatsappGroupStatus.PENDING_CREATE;

  const toggleMode = (always: boolean) => {
    logPageClickBi({ ...WHATSAPP_PAGE, button_name: 'toggle_response_mode' });
    const next = always ? WhatsappGroupResponseMode.ALWAYS : WhatsappGroupResponseMode.MENTION_ONLY;
    void setResponseMode(group.id, next).catch(() => setToast(t('settings.whatsapp.error')));
  };

  return (
    <div className={styles.panel}>
      <header className={styles.head}>
        <Badge variant={badge.variant} label={t(badge.labelKey)} />
        <NameRow group={group} onError={setToast} />
        <Typography variant="p2" color="secondary">
          {subtitle(group)}
        </Typography>
      </header>

      {pending ? (
        <Typography variant="p2" color="secondary">
          {t('settings.whatsapp.group.pending.explainer')}
        </Typography>
      ) : (
        <>
          <section className={styles.section}>
            <Card variant="outline" size="sm">
              <Switch
                checked={group.responseMode === WhatsappGroupResponseMode.ALWAYS}
                onChange={toggleMode}
                label={t('settings.whatsapp.group.mode.label')}
                description={t(modeLabelKey(group.responseMode))}
              />
            </Card>
            <Typography variant="p3" color="secondary">
              {t('settings.whatsapp.group.mode.caption', { agentName: agent.short })}
            </Typography>
          </section>

          {group.status === WhatsappGroupStatus.ACTIVE && group.inviteLink && (
            <InviteLinkRows groupId={group.id} inviteLink={group.inviteLink} onToast={setToast} />
          )}
        </>
      )}

      <Toast open={!!toast} message={toast ?? ''} onClose={() => setToast(null)} />
    </div>
  );
}

// The name row: the group name with a pencil that swaps it in place for an inline field. Enter (or the
// check) saves the rename — the one field the server lets an owner change after the group exists —
// Escape (or the ✕) reverts. An unchanged or empty name never writes.
function NameRow({ group, onError }: { group: WhatsappGroup; onError: (message: string) => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { rename } = useWhatsappGroupActions();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(group.subject);
  const [saving, setSaving] = useState(false);

  const start = () => {
    logPageClickBi({ ...WHATSAPP_PAGE, button_name: 'edit_group_name' });
    setDraft(group.subject);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(group.subject);
  };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === group.subject.trim()) {
      cancel();
      return;
    }
    logPageClickBi({ ...WHATSAPP_PAGE, button_name: 'rename_group' });
    setSaving(true);
    try {
      await rename(group.id, trimmed);
      setEditing(false);
    } catch (err) {
      onError(err instanceof GroupActionError && err.guard ? err.guard : t('settings.whatsapp.group.rename.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className={styles.titleRow}>
        <Typography variant="h2" color="strong" className={styles.subject}>
          {group.subject}
        </Typography>
        <IconButton size="small" variant="text" aria-label={t('settings.whatsapp.group.rename')} onClick={start}>
          <Pencil size={16} aria-hidden />
        </IconButton>
      </div>
    );
  }

  return (
    <div className={styles.editRow}>
      <Input
        className={styles.editField}
        aria-label={t('settings.whatsapp.group.editName')}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        maxLength={80}
        autoFocus
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            void save();
          } else if (e.key === 'Escape') {
            cancel();
          }
        }}
      />
      <IconButton size="small" variant="contained" aria-label={t('common.save')} loading={saving} onClick={() => void save()}>
        <Check size={16} aria-hidden />
      </IconButton>
      <IconButton size="small" variant="text" aria-label={t('common.cancel')} disabled={saving} onClick={cancel}>
        <X size={16} aria-hidden />
      </IconButton>
    </div>
  );
}
